import React from 'react'
import { Icon, Chip } from '@arnsbogroup/react-voicearchive'
import { Timestamp } from 'components/datatable'
import { useIntl } from 'react-intl'
import messages from './messages'
import AiFileChip, { setFilesAi, aiFileMessages } from 'components/AiFileChip'

const FileList = ({refetch, deletable = false, files, canMarkAi = false, canUnmarkAi = true}) => {
    if (!(!!files.length)) {
      return null
    }

    const { formatMessage } = useIntl()

    // The files arrive sorted by date, so a supplier working on one task ends up with a header per
    // file once another task's files fall in between. Group by task instead, so every task shows up
    // once, in the order its newest file appears.
    const fileGroups = files.reduce((groups, file) => {
      const group = groups.find(({ task }) => task == file.task)
      if (!group) {
        return [...groups, { task: file.task, userid: file.userid, files: [file] }]
      }
      group.files.push(file)
      return groups
    }, [])

    const toggleAi = (file) => {
      const isAi = !(+file.isai)
      if (!isAi && !confirm(formatMessage(aiFileMessages.confirmUnmark))) {
        return
      }

      setFilesAi([file], isAi)
        .then((errors) => errors.length
          ? toastr.error(errors.join(', '))
          : toastr.success(formatMessage(aiFileMessages.saved)))
        .then(() => refetch())
        .catch((error) => toastr.error(error))
    }

    const DeleteFile = (file) => {
      if(confirm(formatMessage(messages.confirmDelete, { title: file.title }))) {
        const body = new FormData()
        body.append('fileid', file.uid);
        fetch(`/ordertask/removefile`, { 
          method: 'POST',
          body
        })
        .then((response) => response.text())
        .then(() => {
          refetch()
        })
        .catch((error) => toastr.error(error))
      }
    }

    return (
      <div
        className="overflow-hidden"
      >
        { fileGroups.map((group) => (
          <React.Fragment key={group.task}>
            { group.userid &&
              <div className="border-b bg-gray-50 px-4 py-2 text-xs font-medium">
                {group.userid}
              </div>
            }
            { group.files.map((file) => (
              <div key={file.uid} className="flex flex-wrap hover:bg-gray-200 border-b">
                <div className="grow px-4 py-2 space-y-2">
                  <div className=" flex items-center">
                    <a 
                      href={`/download/task/file/${file.uid}`}
                      className="text-primary-darker hover:text-primary truncate"
                    >
                      { file.title }
                    </a>
                    <Chip className={`ml-2 text-white ${ +file.issource ? 'bg-tertiary border-tertiary' : 'bg-secondary border-secondary' }`}>
                      { +file.issource ? 'Source' : 'Supplier' }
                    </Chip>
                    { file.typeString != 'Audio File' &&
                      <Chip className="ml-2 bg-primary text-white">
                        { file.typeString }
                      </Chip>
                    }
                    <AiFileChip {...file} className="ml-2" />
                  </div>
                  { !!file.comments.length &&
                    <div>
                      { file.comments }
                    </div>
                  }
                </div>
                  <div className="shrink-0 px-4 py-2 flex items-center space-x-3">
                    { file.typeString == 'Audio File' && 
                      <>
                        <span title="Duration">{ file.duration }</span>
                        <span title="Bit Rate">{ file.bitrate }</span>
                        { !!(+file.samplerate) && 
                          <span title="Sample Rate">{ file.samplerate }kHz</span>
                        }
                      </>
                    }
                    { !!(+file.wordcount) &&
                      <span title="Word count">{ file.wordcount } words</span>
                    }
                    { file.filesize &&
                      <span title="Filesize">{ file.filesize }</span>
                    }
                    <span>
                      <Timestamp>{file.crdate}</Timestamp>
                    </span>
                    { canMarkAi && deletable && (!(+file.isai) || canUnmarkAi) &&
                      <Icon
                        icon={['fas', 'robot']}
                        className={`cursor-pointer ${+file.isai ? 'text-primary' : 'text-gray-400 hover:text-primary'}`}
                        title={formatMessage(+file.isai ? aiFileMessages.unmarkAsAi : aiFileMessages.markAsAi)}
                        onClick={() => toggleAi(file)}
                      />
                    }
                    { deletable && 
                      <div className="grow text-right">
                        <Icon
                          icon={["fas", "trash"]}
                          state="negative"
                          onClick={() => DeleteFile(file)}
                        />
                      </div>
                    }
                </div>
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
    )
  }

export default FileList